import { initialsFromName } from './initials.js';

/**
 * Task owner helpers (CT-5093 / CT-5094).
 *
 * `task/my-work/list` only returns `assigneeUuid` — not fullName and not isMine.
 *  - `isMine` → compare assigneeUuid to the logged-in user's uuid (token).
 *  - `name`   → resolve via workspace directory (list_filter), uuid fallback.
 */

/** Logged-in user uuid from the Auth0 → /v1/token exchange. */
export function getViewerUuid() {
  try {
    return localStorage.getItem('user_uuid') || null;
  } catch {
    return null;
  }
}

/** `{ [userUuid]: fullName }` from picker/directory rows `{ id, name }`. */
export function nameMapFromUsers(users = []) {
  return Object.fromEntries(
    users.filter((u) => u?.id).map((u) => [u.id, u.name || u.id]),
  );
}

/**
 * Raw owner slot for a task assignee (before {@link mapApiEventToUi}).
 * @param {string|null|undefined} assigneeUuid
 * @param {{ nameById?: Record<string,string>, viewerUuid?: string|null }} [options]
 */
export function buildTaskOwner(assigneeUuid, { nameById = {}, viewerUuid = null } = {}) {
  if (!assigneeUuid) return null;
  const viewer = viewerUuid ?? getViewerUuid();
  const name = nameById[assigneeUuid] ?? assigneeUuid;
  return {
    id: assigneeUuid,
    name,
    is_mine: Boolean(viewer && assigneeUuid === viewer),
  };
}

/** UI owner slot ({ id, name, initials, isMine }) from an assignee uuid. */
export function toUiOwnerFromAssignee(assigneeUuid, options = {}) {
  const raw = buildTaskOwner(assigneeUuid, options);
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name,
    initials: initialsFromName(raw.name, raw.id),
    isMine: raw.is_mine,
  };
}

/** Alias for action hooks — same as {@link getViewerUuid}. */
export const currentUserUuid = getViewerUuid;
